// ─── Shared agent primitives ────────────────────────────────────────────

/** Priority tier for action items. */
export type AgentPriority = 'critical' | 'high' | 'medium' | 'low'

/**
 * Config lifecycle shared by custom analysis agents. Matches the proxy
 * config-state enums verbatim. A `paused` config still exists but the
 * orchestrator skips dispatching runs for it.
 */
export type AgentConfigStatus = 'active' | 'paused' | 'archived'

/**
 * Run lifecycle shared by every agent. Mirrors the proxy's shared
 * `invocations` run-status enum: a run opens in `pending_dispatch`, moves to
 * `running` once claimed, and ends in one terminal state.
 */
export type AgentRunStatus = 'pending_dispatch' | 'running' | 'completed' | 'failed' | 'cancelled'

/**
 * Mirror of the agentic-side `ComponentRef` shape. Carried on
 * `PMPrediction.affected_components` and on the RCA structured report's
 * citations array.
 */
export interface PMComponentRef {
  /** Discriminator — which graph label this ref points at. */
  scope: 'equipment' | 'process_line' | 'tag'
  /** Stable ontology id (`:Equipment.id`, `:Line.id`, or `:Tag.id`). */
  id: string
  /** Display name resolved at emit time; falls back to id when omitted. */
  name?: string
  /** Optional 0..1 confidence the agent has in attributing this component. */
  attribution_confidence?: number
  /**
   * When `false`, render as a plain badge — the id is not a resolvable
   * graph node (a raw tag path / display name that failed lookup).
   * Omitted / `true` keeps the existing linked chip.
   */
  linkable?: boolean
}

/** Stable cache / rewrite key for one insight chip ref. */
export function plantComponentRefKey(ref: Pick<PMComponentRef, 'scope' | 'id'>): string {
  return `${ref.scope}:${ref.id}`
}
